import React, { ReactNode, Suspense, lazy } from 'react';
import {
  Link,
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  App as AntApp,
  ConfigProvider,
  Dropdown,
  Layout,
  LogoutOutlined,
  QuestionCircleOutlined,
  Spin,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
  LineChartOutlined,
  LinkOutlined,
  MailOutlined,
} from './components/antd';
import { CreateOrganization } from './components/auth/CreateOrganization';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { VerifyEmail } from './components/auth/VerifyEmail';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { ResetPassword } from './components/auth/ResetPassword';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import { TourProvider } from './components/onboarding';
import { AutoStartTour } from './components/onboarding/AutoStartTour';
import { useTour } from './hooks/useTour';

import './App.css';
// Lazy load components
const Forecast = lazy(() => import('./pages/Forecast'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'));
const InvitationResponse = lazy(() => import('./pages/InvitationResponse'));
const Tags = lazy(() => import('./pages/Tags'));
const Profile = lazy(() => import('./pages/Profile'));
const QuickBooksIntegration = lazy(() => import('./pages/QuickBooksIntegration'));
const QuickBooksCallback = lazy(() => import('./pages/QuickBooksCallback'));
const Contact = lazy(() => import('./pages/Contact'));

const { Header, Content, Footer } = Layout;

interface AppLayoutProps {
  children: ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { organization } = useOrganization();
  const { startTour } = useTour();

  const userMenuItems = [
    {
      key: 'forecast',
      icon: <LineChartOutlined />,
      label: <Link to='/forecast'>Forecast</Link>,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: <Link to='/profile'>Profile</Link>,
    },
    {
      key: 'organization',
      icon: <TeamOutlined />,
      label: <Link to='/organization-settings'>Organization</Link>,
    },
    {
      key: 'tags',
      icon: <TagsOutlined />,
      label: <Link to='/tags'>Tags</Link>,
    },
    {
      key: 'quickbooks',
      icon: <LinkOutlined />,
      label: <Link to='/quickbooks'>QuickBooks</Link>,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'help-tour',
      icon: <QuestionCircleOutlined />,
      label: 'Help Tour',
      onClick: () => {
        startTour();
      },
    },
    {
      key: 'contact',
      icon: <MailOutlined />,
      label: <Link to='/contact'>Contact Us</Link>,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  return (
    <div
      style={{ padding: '16px', minHeight: '100vh', boxSizing: 'border-box', background: '#fff' }}
    >
      <Layout style={{ minHeight: 'calc(100vh - 32px)', background: '#fff' }}>
        <Header
          className='site-layout-background'
          style={{
            padding: '0 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
          }}
        >
          <div style={{ fontSize: '20px', fontWeight: 600, color: '#001529' }}>
            <Link to='/forecast' style={{ color: '#001529', textDecoration: 'none' }}>
              Cashflow
            </Link>
          </div>
          <div style={{ fontSize: '16px', fontWeight: 500, color: '#001529' }}>
            {organization?.name}
          </div>
          <div>
            <Dropdown menu={{ items: userMenuItems }} placement='bottomRight'>
              <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <UserOutlined style={{ marginRight: 8 }} />
                {user?.first_name} {user?.last_name}
              </span>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: 0, padding: '24px' }}>
          <div style={{ minHeight: 'calc(100vh - 64px - 70px - 48px)' }}>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Spin size='large' />
                </div>
              }
            >
              {children}
            </Suspense>
          </div>
        </Content>
        <Footer style={{ textAlign: 'center', fontSize: '12px', color: '#888' }}>
          Cashflow Manager ©{new Date().getFullYear()}
          {process.env.REACT_APP_BUILD_VERSION && (
            <span style={{ marginLeft: '10px' }}>v{process.env.REACT_APP_BUILD_VERSION}</span>
          )}
          {process.env.NODE_ENV === 'development' && process.env.REACT_APP_BUILD_TIME && (
            <span style={{ marginLeft: '10px', fontSize: '10px' }}>
              (Built: {new Date(process.env.REACT_APP_BUILD_TIME).toLocaleString()})
            </span>
          )}
        </Footer>
      </Layout>
    </div>
  );
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div>Loading...</div>; // Or your loading component
  }

  if (!user) {
    // Use Navigate to maintain the current domain context
    // The state will be preserved for the redirect back after login
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // If user has no organization and is not on the profile page, redirect to profile
  if (!user.current_organization && location.pathname !== '/profile') {
    return <Navigate to='/profile' replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <Route path='/login' element={<Login />} />
      <Route path='/register' element={<Register />} />
      <Route path='/verify-email/:token' element={<VerifyEmail />} />
      <Route path='/forgot-password' element={<ForgotPassword />} />
      <Route path='/reset-password/:token' element={<ResetPassword />} />
      <Route path='/create-organization' element={<CreateOrganization />} />
      <Route path='/invite/:token' element={<InvitationResponse />} />
      <Route
        path='/forecast'
        element={
          <ProtectedRoute>
            <AppLayout>
              <Forecast />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/profile'
        element={
          <ProtectedRoute>
            <AppLayout>
              <Profile />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/organization-settings'
        element={
          <ProtectedRoute>
            <AppLayout>
              <OrganizationSettings />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/tags'
        element={
          <ProtectedRoute>
            <AppLayout>
              <Tags />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/quickbooks'
        element={
          <ProtectedRoute>
            <AppLayout>
              <QuickBooksIntegration />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/contact'
        element={
          <ProtectedRoute>
            <AppLayout>
              <Contact />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/qbo/callback'
        element={
          <ProtectedRoute>
            <QuickBooksCallback />
          </ProtectedRoute>
        }
      />
      <Route path='/' element={<Navigate to='/forecast' replace />} />
      <Route path='*' element={<Navigate to='/forecast' replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1890ff',
          colorSuccess: '#52c41a',
          colorWarning: '#faad14',
          colorError: '#ff4d4f',
        },
      }}
    >
      <Router>
        <AuthProvider>
          <OrganizationProvider>
            <TourProvider>
              <AntApp>
                <AutoStartTour />
                <AppRoutes />
              </AntApp>
            </TourProvider>
          </OrganizationProvider>
        </AuthProvider>
      </Router>
    </ConfigProvider>
  );
};

// Add type definition for Routes with future flag
declare module 'react-router-dom' {
  interface RoutesProps {
    future?: {
      v7_relativeSplatPath?: boolean;
      v7_startTransition?: boolean;
    };
  }
}

export default App;
